import { Anchor, Button, constants, useWindowSize } from "@newsela/angelou";
import Link from "next/link";
import { useRouter } from "next/router";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { OWN_ORIGIN } from "@/base/base.constants";
import ShareAction from "@/view/SecondaryActions/ShareAction";

import BookmarkButton from "@/components/BookmarkButton";
import getContentLabel from "@/utils/getContentLabel";
import { LangstonContentResponse } from "@/types/models";
import { useAttachmentsContext } from "@/bundles/providers/useAttachmentsContext";
import { useBundleDataContext } from "@/bundles/providers/useBundleDataContext";
import findCurrentContentIdTreeRoute from "@/bundles/utils/findCurrentContentIdTreeRoute";
import {
  headerActionsContainerStyles,
  bookmarkIconStyles,
  shareActionWrapperStyles,
  assignmentButtonWrapperCss,
  popupOverridesStyles,
  rootContainerCss,
  viewResourcesButtonCss,
} from "./HeaderActions.style";

const AnchorWithTrackEvent = withTrackEvent(Anchor);
const ButtonWithTrackEvent = withTrackEvent(Button);

const CONTENT_LABELS = {
  BROWSE_PAGE: "Browse page",
  ROW: "Row",
};

function getAssignmentUrl(routerAsPath: string, contentId: string): string {
  const returnUrl = `${OWN_ORIGIN}${routerAsPath}`;
  return `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${contentId}`;
}

function getBundleLabel(
  content: LangstonContentResponse | null | undefined,
): string | null {
  if (!content?.data || content.data.content_type !== "BUNDLE") return null;
  // The compiler knows about `label` because we discriminated the value using
  // `content_type` above.
  return content.data.label;
}

const HeaderActions = ({
  content,
  currentContentId,
}: {
  content: LangstonContentResponse | null | undefined;
  currentContentId: string | undefined;
}) => {
  const { asPath } = useRouter();
  const { contentOutline, currentContentIdTreeRouteOrDefault } =
    useBundleDataContext();
  const { hasNoAttachments, isResourcePanelOpen, setIsResourcePanelOpen } =
    useAttachmentsContext();
  const bundleLabel = getBundleLabel(content) || "";
  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { lg },
    },
  } = constants;
  const isMobile = windowWidth <= lg;

  const contentTypeLabel = getContentLabel({
    content_type: content?.data?.content_type || "BUNDLE",
    label: "",
  });

  const shouldHideActions =
    bundleLabel.toLowerCase() === CONTENT_LABELS.BROWSE_PAGE.toLowerCase() ||
    bundleLabel.toLowerCase() === CONTENT_LABELS.ROW.toLowerCase();

  if (shouldHideActions) return null;

  const handleOpenResourcePanel = () => {
    setIsResourcePanelOpen(true);
  };

  const currentItemTreePath = findCurrentContentIdTreeRoute(
    contentOutline?.items ?? {},
    currentContentIdTreeRouteOrDefault,
  );

  const isFolder =
    contentOutline?.items[currentContentIdTreeRouteOrDefault]?.isFolder ??
    contentOutline?.items[currentItemTreePath ?? ""]?.isFolder;

  return (
    <div css={rootContainerCss}>
      <div css={headerActionsContainerStyles}>
        <div>
          <BookmarkButton
            contentId={currentContentId}
            iconColor="currentColor"
            hoverLabel="Save"
            __cssFor={{
              Icon: bookmarkIconStyles,
            }}
          />
        </div>
        <div css={shareActionWrapperStyles}>
          <ShareAction
            contentId={currentContentId}
            contentType={contentTypeLabel}
            title={content?.data?.name || ""}
            hoverLabel="Share"
            isMobile={isMobile}
            thumbnail={content?.data?.thumbnail || ""}
            popupOverrides={popupOverridesStyles}
          />
        </div>
        {isFolder === false && currentContentId && (
          <div css={assignmentButtonWrapperCss}>
            <AnchorWithTrackEvent
              href={getAssignmentUrl(asPath, currentContentId)}
              styleAnchorAsButton={{
                flavor: Button.flavors.primary,
                size: Button.sizes.md,
              }}
              objectType={objectTypes.content}
              actionName={actionNames.navigate}
              streamProperties={{
                description:
                  "Navigate to create new assignment from bundle content page",
              }}
              __renderCustomLink={({ children, href, ...anchorProps }) => {
                return (
                  <Link
                    href={typeof href === "string" ? href : ""}
                    data-qa-selector="bundle-page-create-assignment-link"
                    {...anchorProps}
                  >
                    {children}
                  </Link>
                );
              }}
            >
              Create assignment
            </AnchorWithTrackEvent>
          </div>
        )}
      </div>
      <>
        {hasNoAttachments ? null : (
          <>
            <ButtonWithTrackEvent
              flavor={Button.flavors.secondary}
              onClick={handleOpenResourcePanel}
              disabled={isResourcePanelOpen}
              __cssFor={viewResourcesButtonCss}
              objectType={objectTypes.content}
              actionPrefix={actionPrefixes.initiate}
              actionName={actionNames.interact}
              streamProperties={{
                description: "Open the resource panel on the bundles page",
              }}
            >
              View Resources
            </ButtonWithTrackEvent>
          </>
        )}
      </>
    </div>
  );
};

export default HeaderActions;
